import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Tabs, Tab, Button, TextField, Alert, CircularProgress,
  TableBody, TableCell, TableHead, TableRow, Divider
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import { formatAmount } from '../../utils/money';
import ScrollTable from '../ScrollTable';
import TrialBalance from './TrialBalance';
import { downloadWorkbook, safeFilename } from '../../utils/excelExport';

// Bracket negatives — textbook presentation.
const fmt = (n) => formatAmount(n);
const acc = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  const v = Number(n);
  if (Math.abs(v) < 0.005) return '—';
  return v < 0 ? `(${fmt(-v)})` : fmt(v);
};

const toISO = (d) => new Date(d).toISOString().slice(0, 10);

const TABS = [
  'Profit or Loss and OCI',
  'Changes in Equity',
  'Cash Flow Statement',
  'Financial Position',
  'Trial Balance'
];

const Header = ({ companyName, title, subtitle, currency }) => (
  <Box mb={2}>
    <Typography variant="h6" fontWeight={700} sx={{ fontStyle: 'italic' }}>
      {companyName || 'Company'}
    </Typography>
    <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
    )}
    {currency && (
      <Typography variant="caption" color="text.secondary">All figures in {currency}</Typography>
    )}
  </Box>
);

/**
 * Agency financial statements in the textbook IFRS layout (Bagalu-style sample):
 * SoPL with turnover / gross profit / opex / tax; SoCIE; cash flows (indirect);
 * statement of financial position with current/non-current and comparatives.
 *
 * Every figure is loaded from the ledger APIs — nothing is hardcoded.
 */
const FinancialStatements = ({ initialTab = 0 } = {}) => {
  const [tab, setTab] = useState(Math.min(Math.max(Number(initialTab) || 0, 0), TABS.length - 1));
  const [fiscalYear, setFiscalYear] = useState(null);
  const [range, setRange] = useState({ from: '', to: '' });
  const [applied, setApplied] = useState({ from: '', to: '' });
  const [asOf, setAsOf] = useState(toISO(new Date()));
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/accounting/fiscal-year');
        setFiscalYear(res.data);
        const start = toISO(res.data.start);
        const end = new Date(res.data.end) < new Date() ? toISO(res.data.end) : toISO(new Date());
        setRange({ from: start, to: end });
        setApplied({ from: start, to: end });
        setAsOf(end);
      } catch (e) {
        toast.error('Could not read the financial year');
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (tab === 4) return;
    if (tab === 3) {
      setLoading(true);
      try {
        const res = await axios.get('/accounting/reports/financial-position', { params: { asOf } });
        setData((d) => ({ ...d, sofP: res.data }));
      } catch (e) {
        toast.error(e.response?.data?.message || 'Could not load the statement of financial position');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!applied.from || !applied.to) return;
    setLoading(true);
    try {
      const params = { from: applied.from, to: applied.to };
      if (tab === 0) {
        const res = await axios.get('/accounting/reports/income-statement', { params });
        setData((d) => ({ ...d, pl: res.data }));
      } else if (tab === 1) {
        const res = await axios.get('/accounting/reports/changes-in-equity', { params });
        setData((d) => ({ ...d, equity: res.data }));
      } else {
        const res = await axios.get('/accounting/reports/cash-flow', {
          params: { startDate: applied.from, endDate: applied.to }
        });
        setData((d) => ({ ...d, cash: res.data }));
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load the statement');
    } finally {
      setLoading(false);
    }
  }, [applied, tab, asOf]);

  useEffect(() => { load(); }, [load]);

  const preset = async (which) => {
    const today = new Date();
    let from; let to;
    if (which === 'fy') {
      if (!fiscalYear) return;
      from = toISO(fiscalYear.start);
      to = new Date(fiscalYear.end) < today ? toISO(fiscalYear.end) : toISO(today);
    } else if (which === 'lastFy') {
      if (!fiscalYear) return;
      const dayBefore = new Date(new Date(fiscalYear.start).getTime() - 24 * 60 * 60 * 1000);
      try {
        const res = await axios.get('/accounting/fiscal-year', { params: { at: toISO(dayBefore) } });
        from = toISO(res.data.start);
        to = toISO(res.data.end);
      } catch (e) {
        toast.error('Could not read the previous financial year');
        return;
      }
    } else if (which === 'month') {
      from = toISO(new Date(today.getFullYear(), today.getMonth(), 1));
      to = toISO(today);
    } else {
      from = toISO(new Date(today.getFullYear(), today.getMonth() - 1, 1));
      to = toISO(new Date(today.getFullYear(), today.getMonth(), 0));
    }
    setRange({ from, to });
    setApplied({ from, to });
    setAsOf(to);
  };

  const handleDownload = async () => {
    try {
      if (tab === 0 && data.pl) {
        const pl = data.pl;
        const rows = [
          { line: 'Turnover', note: pl.face?.turnover?.note, amount: pl.face?.turnover?.total, prior: pl.prior?.totalIncome },
          { line: 'Direct costs', note: pl.face?.directCosts?.note, amount: pl.face?.directCosts?.total, prior: pl.prior?.directCosts ?? pl.face?.directCosts?.priorTotal },
          { line: 'Gross profit', note: '', amount: pl.grossProfit, prior: pl.priorGrossProfit },
          { line: 'Staff costs', note: pl.face?.staffCosts?.note, amount: pl.face?.staffCosts?.total, prior: pl.face?.staffCosts?.priorTotal },
          { line: 'Administrative expenses', note: pl.face?.administrative?.note, amount: pl.face?.administrative?.total, prior: pl.face?.administrative?.priorTotal },
          { line: 'Finance and other costs', note: pl.face?.financeCosts?.note, amount: pl.face?.financeCosts?.total, prior: pl.face?.financeCosts?.priorTotal },
          { line: 'Operating profit before tax', note: '', amount: pl.profitBeforeTax, prior: pl.priorProfitBeforeTax },
          { line: 'Taxation', note: pl.taxNote, amount: pl.taxation, prior: pl.priorTaxation },
          { line: 'Profit after taxation', note: '', amount: pl.profitAfterTax, prior: pl.priorProfitAfterTax },
          { line: 'Other comprehensive income', note: '', amount: pl.otherComprehensiveIncome, prior: pl.priorOtherComprehensiveIncome },
          { line: 'Total comprehensive income', note: '', amount: pl.totalComprehensiveIncome, prior: pl.priorTotalComprehensiveIncome }
        ];
        await downloadWorkbook({
          filename: safeFilename(['profit-or-loss', applied.from, applied.to]),
          sheets: [
            { name: 'Profit or Loss', rows },
            ...(pl.notes || []).map((n) => ({
              name: `Note ${n.number}`,
              rows: [
                ...n.accounts.map((a) => ({ account: `${a.code} ${a.name}`, amount: a.amount, prior: a.priorAmount })),
                { account: 'Total', amount: n.total, prior: n.priorTotal }
              ]
            }))
          ]
        });
      } else if (tab === 1 && data.equity) {
        const st = data.equity;
        await downloadWorkbook({
          filename: safeFilename(['changes-in-equity', applied.from, applied.to]),
          sheets: [{
            name: 'Changes in Equity',
            rows: st.rows.map((r) => ({
              movement: r.label,
              'Share capital': r.shareCapital ?? r.cells?.[0],
              'Retained earnings': r.retainedEarnings ?? r.cells?.[1],
              'Current earnings': r.currentEarnings,
              Total: r.total
            }))
          }]
        });
      } else if (tab === 2 && data.cash) {
        const cf = data.cash;
        const rows = [
          { line: 'Profit before taxation', amount: cf.operating?.profitBeforeTax },
          ...(cf.operating?.adjustments || []).map((a) => ({ line: a.label, amount: a.amount })),
          ...(cf.operating?.workingCapital || []).map((a) => ({ line: a.label, amount: a.amount })),
          { line: 'Cash generated from operations', amount: cf.operating?.cashGeneratedFromOperations },
          { line: 'Net cash from operating activities', amount: cf.operating?.netCashFromOperatingActivities },
          ...(cf.investing?.lines || []).map((a) => ({ line: a.label, amount: a.amount })),
          { line: 'Net cash from investing activities', amount: cf.investing?.netCashFromInvestingActivities },
          ...(cf.financing?.lines || []).map((a) => ({ line: a.label, amount: a.amount })),
          { line: 'Net cash from financing activities', amount: cf.financing?.netCashFromFinancingActivities },
          { line: 'Net increase/(decrease) in cash', amount: cf.netIncrease },
          { line: 'Cash at beginning of period', amount: cf.openingCash },
          { line: 'Cash at end of period', amount: cf.closingCash }
        ];
        await downloadWorkbook({
          filename: safeFilename(['cash-flow', applied.from, applied.to]),
          sheets: [{ name: 'Cash Flow', rows }]
        });
      } else if (tab === 3 && data.sofP) {
        const s = data.sofP;
        const c = s.current;
        const p = s.prior;
        const rows = [
          ...(c.nonCurrentAssets || []).map((g) => ({ line: g.label, note: g.note, amount: g.amount, prior: null })),
          { line: 'Total non-current assets', amount: c.totalNonCurrentAssets, prior: p?.totalNonCurrentAssets },
          ...(c.currentAssets || []).map((g) => ({ line: g.label, note: g.note, amount: g.amount, prior: null })),
          { line: 'Total current assets', amount: c.totalCurrentAssets, prior: p?.totalCurrentAssets },
          { line: 'TOTAL ASSETS', amount: c.totalAssets, prior: p?.totalAssets },
          { line: 'Share capital', note: s.notes?.shareCapital, amount: c.shareCapital, prior: p?.shareCapital },
          { line: 'Retained earnings', note: s.notes?.retainedEarnings, amount: c.retainedEarnings, prior: p?.retainedEarnings },
          { line: 'Total equity', amount: c.totalEquity, prior: p?.totalEquity },
          ...(c.nonCurrentLiabilities || []).map((g) => ({ line: g.label, amount: g.amount })),
          ...(c.currentLiabilities || []).map((g) => ({ line: g.label, note: g.note, amount: g.amount })),
          { line: 'TOTAL EQUITY AND LIABILITIES', amount: c.totalEquityAndLiabilities, prior: p?.totalEquityAndLiabilities }
        ];
        await downloadWorkbook({
          filename: safeFilename(['financial-position', asOf]),
          sheets: [{ name: 'Financial Position', rows }]
        });
      } else {
        toast.info('Use the download on the statement itself');
        return;
      }
    } catch (e) {
      toast.error('Could not build the Excel file');
    }
  };

  const CompCols = ({ year, priorYear }) => (
    <TableHead>
      <TableRow>
        <TableCell>Description</TableCell>
        <TableCell align="center" width={56}>Notes</TableCell>
        <TableCell align="right">{year} {data.pl?.currency || data.sofP?.currency || 'UGX'}</TableCell>
        {priorYear && (
          <TableCell align="right">{priorYear} {data.pl?.currency || data.sofP?.currency || 'UGX'}</TableCell>
        )}
      </TableRow>
    </TableHead>
  );

  const FaceRow = ({ label, note, amount, prior, bold, indent, double }) => (
    <TableRow sx={double ? { '& td': { borderBottom: '3px double', borderColor: 'text.primary' } } : undefined}>
      <TableCell sx={{ fontWeight: bold ? 700 : 400, pl: indent ? 4 : 2 }}>{label}</TableCell>
      <TableCell align="center">{note || ''}</TableCell>
      <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>{acc(amount)}</TableCell>
      {prior !== undefined && (
        <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: bold ? 700 : 400 }}>
          {prior === null ? '' : acc(prior)}
        </TableCell>
      )}
    </TableRow>
  );

  const SectionHead = ({ children, cols }) => (
    <TableRow>
      <TableCell colSpan={cols} sx={{ fontWeight: 700, bgcolor: 'action.hover', textTransform: 'uppercase' }}>
        {children}
      </TableCell>
    </TableRow>
  );

  const renderProfitAndLoss = () => {
    const pl = data.pl;
    if (!pl?.face) return null;
    const hasPrior = Boolean(pl.prior);
    const cols = hasPrior ? 4 : 3;
    return (
      <Box>
        <Header
          companyName={pl.companyName}
          title={pl.title}
          subtitle={`For the period ${pl.label}`}
          currency={pl.currency}
        />
        <ScrollTable containerProps={{ component: Paper, variant: 'outlined' }} size="small">
          <CompCols year={pl.yearLabel} priorYear={hasPrior ? pl.prior.yearLabel : null} />
          <TableBody>
            <FaceRow label="Turnover" note={pl.face.turnover.note} amount={pl.face.turnover.total} prior={hasPrior ? pl.prior.totalIncome : undefined} />
            <FaceRow label="Less: Direct costs" note={pl.face.directCosts.note} amount={pl.face.directCosts.total} prior={hasPrior ? pl.face.directCosts.priorTotal : undefined} indent />
            <FaceRow label="Gross profit" amount={pl.grossProfit} prior={hasPrior ? pl.priorGrossProfit : undefined} bold />
            <SectionHead cols={cols}>Less: Operating expenses</SectionHead>
            <FaceRow label="Staff costs" note={pl.face.staffCosts.note} amount={pl.face.staffCosts.total} prior={hasPrior ? pl.face.staffCosts.priorTotal : undefined} indent />
            <FaceRow label="Administrative expenses" note={pl.face.administrative.note} amount={pl.face.administrative.total} prior={hasPrior ? pl.face.administrative.priorTotal : undefined} indent />
            <FaceRow label="Finance and other costs" note={pl.face.financeCosts.note} amount={pl.face.financeCosts.total} prior={hasPrior ? pl.face.financeCosts.priorTotal : undefined} indent />
            <FaceRow label="Total operating expenses" amount={pl.operatingExpenses} prior={hasPrior ? pl.priorOperatingExpenses : undefined} bold />
            <FaceRow label="Operating profit /(loss) before taxation" amount={pl.profitBeforeTax} prior={hasPrior ? pl.priorProfitBeforeTax : undefined} bold />
            <FaceRow label="Taxation" note={pl.taxNote} amount={pl.taxation} prior={hasPrior ? pl.priorTaxation : undefined} />
            <FaceRow label="Profit /(loss) after taxation" amount={pl.profitAfterTax} prior={hasPrior ? pl.priorProfitAfterTax : undefined} bold double />
            <SectionHead cols={cols}>Other comprehensive income</SectionHead>
            <FaceRow label="Items that will not be reclassified to profit or loss" amount={0} prior={hasPrior ? 0 : undefined} indent />
            <FaceRow label="Items that may be reclassified subsequently to profit or loss" amount={0} prior={hasPrior ? 0 : undefined} indent />
            <FaceRow label="Other comprehensive income for the period" amount={pl.otherComprehensiveIncome} prior={hasPrior ? pl.priorOtherComprehensiveIncome : undefined} bold />
            <FaceRow label="Total comprehensive income for the period" amount={pl.totalComprehensiveIncome} prior={hasPrior ? pl.priorTotalComprehensiveIncome : undefined} bold double />
          </TableBody>
        </ScrollTable>

        <Typography variant="subtitle2" fontWeight={700} mt={3} gutterBottom>Notes to the statement</Typography>
        {(pl.notes || []).filter((n) => n.accounts?.length || Math.abs(n.total) > 0.001).map((n) => (
          <Box key={n.number} mb={2}>
            <Typography variant="body2" fontWeight={700}>{n.number}. {n.title}</Typography>
            <ScrollTable containerProps={{ component: Paper, variant: 'outlined' }} size="small">
              <TableBody>
                {n.accounts.map((a) => (
                  <TableRow key={a.code}>
                    <TableCell sx={{ pl: 4 }}>{a.code} {a.name}</TableCell>
                    <TableCell align="right">{acc(a.amount)}</TableCell>
                    {hasPrior && <TableCell align="right" sx={{ color: 'text.secondary' }}>{acc(a.priorAmount)}</TableCell>}
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, pl: 4 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{acc(n.total)}</TableCell>
                  {hasPrior && <TableCell align="right" sx={{ fontWeight: 700 }}>{acc(n.priorTotal)}</TableCell>}
                </TableRow>
              </TableBody>
            </ScrollTable>
          </Box>
        ))}
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Agency result only. Rent collected for landlords is a liability, not turnover.
          Taxation is the net movement on Taxes Owed ({pl.taxNote}) for the period.
          The year-end close is shown on the statement of changes in equity, not here.
        </Typography>
      </Box>
    );
  };

  const renderChangesInEquity = () => {
    const st = data.equity;
    if (!st) return null;
    return (
      <Box>
        <Header
          companyName={st.companyName}
          title={st.title || 'Statement of Changes in Equity'}
          subtitle={`For the period ${st.label}`}
          currency={st.currency}
        />
        {!st.reconciled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Closing equity differs from the statement of financial position by {fmt(st.difference)}.
            Investigate before relying on these figures.
          </Alert>
        )}
        <ScrollTable containerProps={{ component: Paper, variant: 'outlined' }} size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              {st.columns.map((c) => (
                <TableCell key={c.key || c.code} align="right">{c.name}</TableCell>
              ))}
              <TableCell align="right">Current earnings</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {st.rows.map((r) => {
              const bold = ['opening', 'closing', 'comprehensive'].includes(r.key);
              return (
                <TableRow
                  key={r.key}
                  sx={r.key === 'closing' ? { '& td': { borderTop: '3px double', borderColor: 'text.primary' } } : undefined}
                >
                  <TableCell sx={{ fontWeight: bold ? 700 : 400 }}>{r.label}</TableCell>
                  {r.cells.map((v, i) => (
                    <TableCell key={st.columns[i].key || st.columns[i].code} align="right" sx={{ fontWeight: bold ? 700 : 400 }}>
                      {acc(v)}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>{acc(r.currentEarnings)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{acc(r.total)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </ScrollTable>
      </Box>
    );
  };

  const renderCashFlow = () => {
    const cf = data.cash;
    if (!cf?.operating) return null;
    const Line = ({ label, amount, bold, indent, double }) => (
      <TableRow sx={double ? { '& td': { borderBottom: '3px double', borderColor: 'text.primary' } } : undefined}>
        <TableCell sx={{ fontWeight: bold ? 700 : 400, pl: indent ? 4 : 2 }}>{label}</TableCell>
        <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>{acc(amount)}</TableCell>
      </TableRow>
    );
    return (
      <Box>
        <Header
          companyName={cf.companyName}
          title={cf.title || 'Statement of Cash Flows'}
          subtitle={`For the period ${cf.label} (IAS 7 — indirect method)`}
          currency={cf.currency}
        />
        <ScrollTable containerProps={{ component: Paper, variant: 'outlined' }} size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell align="right">{cf.yearLabel} {cf.currency || 'UGX'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <SectionHead cols={2}>Cash flows from operating activities</SectionHead>
            <Line label="Profit before taxation" amount={cf.operating.profitBeforeTax} />
            <Line label="Adjustments for non-cash items:" bold />
            {cf.operating.adjustments.map((a) => (
              <Line key={a.label} label={a.label} amount={a.amount} indent />
            ))}
            <Line label="Operating profit before working capital changes" amount={cf.operating.afterAdjustments} bold />
            <Line label="Working capital changes:" bold />
            {cf.operating.workingCapital.map((a) => (
              <Line key={a.label} label={a.label} amount={a.amount} indent />
            ))}
            <Line label="Cash generated from /(used in) operations" amount={cf.operating.cashGeneratedFromOperations} bold />
            <Line label="Interest paid" amount={cf.operating.interestPaid} indent />
            <Line label="Income taxes paid" amount={cf.operating.incomeTaxesPaid} indent />
            <Line label="Net cash from /(used in) operating activities" amount={cf.operating.netCashFromOperatingActivities} bold />

            <SectionHead cols={2}>Cash flows from investing activities</SectionHead>
            {cf.investing.lines.map((a) => (
              <Line key={a.label} label={a.label} amount={a.amount} indent />
            ))}
            <Line label="Net cash from /(used in) investing activities" amount={cf.investing.netCashFromInvestingActivities} bold />

            <SectionHead cols={2}>Cash flows from financing activities</SectionHead>
            {cf.financing.lines.map((a) => (
              <Line key={a.label} label={a.label} amount={a.amount} indent />
            ))}
            <Line label="Net cash from /(used in) financing activities" amount={cf.financing.netCashFromFinancingActivities} bold />

            <Line label="Net increase /(decrease) in cash and cash equivalents" amount={cf.netIncrease} bold />
            <Line label="Cash and cash equivalents at the beginning of the period" amount={cf.openingCash} />
            {Math.abs(cf.nonFlowMovements || 0) >= 0.01 && (
              <Line label="Opening balances and other non-cash postings" amount={cf.nonFlowMovements} />
            )}
            <Line label="Cash and cash equivalents at the end of the period" amount={cf.closingCash} bold double />
          </TableBody>
        </ScrollTable>
      </Box>
    );
  };

  const renderFinancialPosition = () => {
    const s = data.sofP;
    if (!s?.current) return null;
    const c = s.current;
    const p = s.prior;
    const hasPrior = Boolean(p);
    const cols = hasPrior ? 4 : 3;
    const priorAmt = (key) => (hasPrior ? p[key] : undefined);
    const priorGroup = (label) => {
      if (!hasPrior) return undefined;
      const g = [...(p.nonCurrentAssets || []), ...(p.currentAssets || []), ...(p.nonCurrentLiabilities || []), ...(p.currentLiabilities || [])]
        .find((x) => x.label === label);
      return g ? g.amount : 0;
    };
    return (
      <Box>
        <Header
          companyName={s.companyName}
          title={s.title}
          subtitle={`As at ${s.label}`}
          currency={s.currency}
        />
        {!c.balanced && (
          <Alert severity="error" sx={{ mb: 2 }}>
            The statement does not balance (difference {fmt(c.difference)}). An unbalanced
            journal may have reached the ledger.
          </Alert>
        )}
        <Box mb={2} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            type="date" label="As at" size="small" value={asOf}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setAsOf(e.target.value)}
          />
          <Button size="small" variant="contained" onClick={load}>Apply</Button>
        </Box>
        <ScrollTable containerProps={{ component: Paper, variant: 'outlined' }} size="small">
          <CompCols year={c.yearLabel} priorYear={hasPrior ? p.yearLabel : null} />
          <TableBody>
            <SectionHead cols={cols}>Assets</SectionHead>
            <SectionHead cols={cols}>Non-current assets</SectionHead>
            {(c.nonCurrentAssets || []).map((g) => (
              <FaceRow key={g.label} label={g.label} note={g.note} amount={g.amount} prior={priorGroup(g.label)} indent />
            ))}
            <FaceRow label="Total non-current assets" amount={c.totalNonCurrentAssets} prior={priorAmt('totalNonCurrentAssets')} bold />
            <SectionHead cols={cols}>Current assets</SectionHead>
            {(c.currentAssets || []).map((g) => (
              <FaceRow key={g.label} label={g.label} note={g.note} amount={g.amount} prior={priorGroup(g.label)} indent />
            ))}
            <FaceRow label="Total current assets" amount={c.totalCurrentAssets} prior={priorAmt('totalCurrentAssets')} bold />
            <FaceRow label="TOTAL ASSETS" amount={c.totalAssets} prior={priorAmt('totalAssets')} bold double />

            <SectionHead cols={cols}>Financed by</SectionHead>
            <SectionHead cols={cols}>Equity</SectionHead>
            <FaceRow label="Share capital / Owner equity" note={s.notes?.shareCapital} amount={c.shareCapital} prior={priorAmt('shareCapital')} indent />
            <FaceRow label="Retained earnings" note={s.notes?.retainedEarnings} amount={c.retainedEarnings} prior={priorAmt('retainedEarnings')} indent />
            <FaceRow label="Total equity" amount={c.totalEquity} prior={priorAmt('totalEquity')} bold />

            <SectionHead cols={cols}>Non-current liabilities</SectionHead>
            {(c.nonCurrentLiabilities || []).length === 0 && (
              <FaceRow label="—" amount={0} prior={hasPrior ? 0 : undefined} indent />
            )}
            {(c.nonCurrentLiabilities || []).map((g) => (
              <FaceRow key={g.label} label={g.label} note={g.note} amount={g.amount} prior={priorGroup(g.label)} indent />
            ))}
            <FaceRow label="Total non-current liabilities" amount={c.totalNonCurrentLiabilities} prior={priorAmt('totalNonCurrentLiabilities')} bold />

            <SectionHead cols={cols}>Current liabilities</SectionHead>
            {(c.currentLiabilities || []).map((g) => (
              <FaceRow key={g.label} label={g.label} note={g.note} amount={g.amount} prior={priorGroup(g.label)} indent />
            ))}
            <FaceRow label="Total current liabilities" amount={c.totalCurrentLiabilities} prior={priorAmt('totalCurrentLiabilities')} bold />
            <FaceRow label="Total liabilities" amount={c.totalLiabilities} prior={priorAmt('totalLiabilities')} bold />
            <FaceRow label="TOTAL EQUITY AND LIABILITIES" amount={c.totalEquityAndLiabilities} prior={priorAmt('totalEquityAndLiabilities')} bold double />
          </TableBody>
        </ScrollTable>
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          The notes form an integral part of these financial statements. Landlord funds held
          appear under current liabilities — they are money owed to clients, not equity.
        </Typography>
      </Box>
    );
  };

  const showPeriod = tab <= 2;

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{
          mb: 2,
          '& .MuiTabs-flexContainer': { flexWrap: 'wrap' },
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTab-root': { borderBottom: '2px solid transparent', '&.Mui-selected': { borderBottomColor: 'primary.main' } }
        }}
      >
        {TABS.map((label) => <Tab key={label} label={label} />)}
      </Tabs>

      {(showPeriod || tab === 3) && (
        <Box mb={2} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {showPeriod && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Period:</Typography>
              <Button size="small" variant="outlined" onClick={() => preset('fy')} disabled={!fiscalYear}>
                {fiscalYear ? `FY ${fiscalYear.label} to date` : 'Financial year to date'}
              </Button>
              <Button size="small" variant="outlined" onClick={() => preset('lastFy')} disabled={!fiscalYear}>Last financial year</Button>
              <Button size="small" variant="outlined" onClick={() => preset('month')}>This month</Button>
              <Button size="small" variant="outlined" onClick={() => preset('lastMonth')}>Last month</Button>
              <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
                value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
              <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
                value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
              <Button size="small" variant="contained" onClick={() => { setApplied(range); setAsOf(range.to); }} disabled={!range.from || !range.to}>Apply</Button>
            </>
          )}
          <Box flex={1} />
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload} disabled={loading}>
            Download Excel
          </Button>
        </Box>
      )}

      {loading && <Box py={2}><CircularProgress size={24} /></Box>}

      {tab === 0 && renderProfitAndLoss()}
      {tab === 1 && renderChangesInEquity()}
      {tab === 2 && renderCashFlow()}
      {tab === 3 && renderFinancialPosition()}
      {tab === 4 && <TrialBalance />}

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        All amounts are taken from the general ledger. Presentation headings are mapped from
        account codes; rename or add accounts under Chart of Accounts and they appear here
        automatically.
      </Typography>
    </Box>
  );
};

export default FinancialStatements;
